// Ported from himitsuconfidential/downstack-practice (header.js).
// Same shape offsets, same kick tables, same board convention (row 0 = bottom,
// y increases upward) so the generator port in generator/downstack.ts can be
// a near-literal translation of the original algorithm.

export type PieceType = 'I' | 'J' | 'L' | 'O' | 'S' | 'T' | 'Z';
export type Cell = PieceType | 'G' | 'N';
export const PIECE_TYPES: PieceType[] = ['I', 'J', 'L', 'O', 'S', 'T', 'Z'];

export type Offset = readonly [number, number];

export const SHAPE_TABLE: Record<PieceType, readonly [Offset[], Offset[], Offset[], Offset[]]> = {
  I: [
    [[-1, 0], [0, 0], [1, 0], [2, 0]],
    [[0, 1], [0, 0], [0, -1], [0, -2]],
    [[-1, -1], [0, -1], [1, -1], [2, -1]],
    [[1, 1], [1, 0], [1, -1], [1, -2]],
  ],
  J: [
    [[-1, 1], [-1, 0], [0, 0], [1, 0]],
    [[0, 1], [0, 0], [-1, -1], [0, -1]],
    [[-1, 0], [0, 0], [1, 0], [1, -1]],
    [[0, 1], [1, 1], [0, 0], [0, -1]],
  ],
  L: [
    [[1, 1], [-1, 0], [0, 0], [1, 0]],
    [[-1, 1], [0, 1], [0, 0], [0, -1]],
    [[-1, 0], [0, 0], [1, 0], [-1, -1]],
    [[0, 1], [0, 0], [0, -1], [1, -1]],
  ],
  S: [
    [[0, 1], [1, 1], [-1, 0], [0, 0]],
    [[-1, 1], [-1, 0], [0, 0], [0, -1]],
    [[0, 0], [1, 0], [-1, -1], [0, -1]],
    [[0, 1], [0, 0], [1, 0], [1, -1]],
  ],
  Z: [
    [[-1, 1], [0, 1], [0, 0], [1, 0]],
    [[0, 1], [-1, 0], [0, 0], [-1, -1]],
    [[-1, 0], [0, 0], [0, -1], [1, -1]],
    [[1, 1], [0, 0], [1, 0], [0, -1]],
  ],
  O: [
    [[0, 1], [1, 1], [0, 0], [1, 0]],
    [[0, 1], [1, 1], [0, 0], [1, 0]],
    [[0, 1], [1, 1], [0, 0], [1, 0]],
    [[0, 1], [1, 1], [0, 0], [1, 0]],
  ],
  T: [
    [[0, 1], [-1, 0], [0, 0], [1, 0]],
    [[0, 1], [-1, 0], [0, 0], [0, -1]],
    [[-1, 0], [0, 0], [1, 0], [0, -1]],
    [[0, 1], [0, 0], [1, 0], [0, -1]],
  ],
};

// The reference engine stores only one kick table per piece group (it calls
// it the "anticlockwise" table) and derives BOTH rotation directions from it
// — clockwise re-indexes by the new orientation and negates the offset. That
// works because SRS kick offsets are antisymmetric: offset(O→O+1) = -offset(O+1→O).
// See engine/game.ts rotateCW/rotateCCW for the exact indexing ported from
// header.js rotate_clockwise/rotate_anticlockwise.
export const JLTSZ_KICKS: readonly Offset[][] = [
  [[1, 0], [1, 1], [0, -2], [1, -2]],
  [[-1, 0], [-1, -1], [0, 2], [-1, 2]],
  [[-1, 0], [-1, 1], [0, -2], [-1, -2]],
  [[1, 0], [1, -1], [0, 2], [1, 2]],
];
export const JLTSZ_180_KICKS: readonly Offset[][] = [
  [[0, 1]],
  [[-1, 0]],
  [[0, -1]],
  [[1, 0]],
];
export const I_KICKS: readonly Offset[][] = [
  [[-1, 0], [2, 0], [-1, 2], [2, -1]],
  [[-2, 0], [1, 0], [-2, -1], [1, 2]],
  [[1, 0], [-2, 0], [1, -2], [-2, 1]],
  [[2, 0], [-1, 0], [2, 1], [-1, -2]],
];

// T-spin corner tests: 4 corners around the pivot, grouped by orientation
// (front two corners) vs the fixed back-corner pair used by the 3-corner rule.
export const TSPIN_FRONT_CORNERS: readonly Offset[][] = [
  [[-1, 1], [1, 1]],
  [[-1, 1], [-1, -1]],
  [[-1, -1], [1, -1]],
  [[1, 1], [1, -1]],
];
export const TSPIN_ALL_CORNERS: readonly Offset[] = [[-1, -1], [-1, 1], [1, 1], [1, -1]];

export const BOARD_WIDTH = 10;
export const BOARD_HEIGHT = 20;

export const COLOR_TABLE: Record<Cell, string> = {
  I: '#3fd7e8',
  O: '#e3b23c',
  T: '#c15bd4',
  S: '#6fcf5a',
  Z: '#e05b5b',
  J: '#4f6fd6',
  L: '#e08a3c',
  G: '#4a4a52',
  N: 'transparent',
};
